import {
  destinationById,
  resolveDestination,
  type LabelDestination,
  type LabelDestinationId,
} from './destinations.js';
import { printToDymoFromBrowser } from './dymo-browser.js';
import type { LabelJobItem } from './pdf.js';
import type { QueueRow } from './queue.js';

/**
 * The print dispatch: one call that turns the Labeling page's list into ONE job
 * for the chosen destination. Every destination goes through here, so the page
 * never knows how a job is delivered.
 *
 * A job is either a PDF built in the browser (pdf.ts, loaded on demand) for the
 * browser's own print dialog, or one job sent straight to a DYMO LabelWriter
 * through DYMO Connect (dymo-status.ts), with no dialog. {@link PrintOutcome}
 * has a variant for each.
 */

/** The printable rows of a list as job items, in list order. */
export function jobItems(rows: readonly QueueRow[]): LabelJobItem[] {
  return rows
    .filter((r) => r.product.printable && r.product.fnsku !== null)
    .map((r) => ({ fnsku: r.product.fnsku as string, title: r.product.title, qty: r.qty }));
}

/** A print that made a PDF document. */
export interface PdfPrintOutcome {
  kind: 'pdf';
  /** The destination that was actually used. */
  destination: LabelDestination;
  /** True when the chosen destination was not available and the default was used. */
  fellBack: boolean;
  fallbackReason: string | null;
  /** The document, for the page to open and to offer as a download. */
  blob: Blob;
  fileName: string;
  labels: number;
  pages: number;
}

/** A print that was sent straight to the DYMO printer. */
export interface DymoPrintOutcome {
  kind: 'dymo';
  destination: LabelDestination;
  /** The printer the job went to. */
  printerName: string;
  /** Labels handed to the printer — sent, which is not the same as seen to come out. */
  labels: number;
  /** Print jobs it took: one for the whole list. */
  jobs: number;
  /** What the printer reported about its roll that the user should know. */
  warnings: string[];
}

/** What a print produced. */
export type PrintOutcome = PdfPrintOutcome | DymoPrintOutcome;

/** The destinations that make a PDF: asking for one of them by name always yields a PDF. */
export type PdfDestinationId = Exclude<LabelDestinationId, 'dymo-30336'>;

export interface PrintOptions {
  startAt?: number;
  now?: Date;
}

/** A file name that sorts by when it was made: fnsku-labels-20261009-1432.pdf. */
export function labelFileName(now: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `fnsku-labels-${stamp}.pdf`;
}

/**
 * Prints the list to the chosen destination as one job. Rows that cannot be
 * printed (no FNSKU on file) are left out; the caller says how many. Throws when
 * there is nothing to print or a label cannot be drawn on the stock.
 *
 * The DYMO destination never falls back by itself: when the printer cannot take
 * the job this throws a `DymoPrintError` saying why and how much was sent, and
 * the page — which still holds the list — offers the PDF. Printing to a
 * different place than the one on screen is not something to do silently.
 */
export function printLabels(
  rows: readonly QueueRow[],
  chosen: PdfDestinationId,
  options?: PrintOptions,
): Promise<PdfPrintOutcome>;
export function printLabels(
  rows: readonly QueueRow[],
  chosen: LabelDestinationId,
  options?: PrintOptions,
): Promise<PrintOutcome>;
export async function printLabels(
  rows: readonly QueueRow[],
  chosen: LabelDestinationId,
  options: PrintOptions = {},
): Promise<PrintOutcome> {
  const items = jobItems(rows);
  if (items.length === 0) throw new Error('There is nothing to print.');

  if (destinationById(chosen).kind === 'dymo') {
    const result = await printToDymoFromBrowser(items);
    return { kind: 'dymo', destination: destinationById(chosen), ...result };
  }

  const { destination, fellBack, reason } = await resolveDestination(chosen);
  // The PDF library is loaded here, on the first print, never with the page.
  const { buildLabelPdf, labelProblem } = await import('./pdf.js');
  for (const item of items) {
    const problem = labelProblem(destination.stock, item.fnsku);
    if (problem) throw new Error(`${item.fnsku}: ${problem}`);
  }
  const pdf = await buildLabelPdf({
    stock: destination.stock,
    items,
    startAt: destination.supportsStartAt ? (options.startAt ?? 1) : 1,
  });
  return {
    kind: 'pdf',
    destination,
    fellBack,
    fallbackReason: reason,
    blob: new Blob([pdf.bytes as BlobPart], { type: 'application/pdf' }),
    fileName: labelFileName(options.now ?? new Date()),
    labels: pdf.labels,
    pages: pdf.pages,
  };
}
